import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
    IconButton, InputLabel, MenuItem,
    Paper, Select,
    Table,
    TableBody, TableCell,
    TableHead,
    TableRow, TextField, useMediaQuery
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from '@shared/config/axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatISOToCustom, isISO8601 } from '@shared/api/utils';
import {
    MAINTENANCE_STATUSES_ARR, MAINTENANCE_TYPE,
    MAINTENANCE_TYPE_ARR
} from '@shared/api/constants';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { LoadingPage } from '@widgets/loading-page';

const StyledTableCell = styled(TableCell)(({ theme }) => ( {
    fontSize: '0.75rem',
    padding: '4px',
    height: '32px',
    [theme.breakpoints.up('md')]: {
        fontSize: '1rem',
        padding: '8px',
        height: '48px',
    },
} ));

const minDate = dayjs('2024-01-01');
const maxDate = dayjs();

export const TasksPage = () => {
    const queryClient = useQueryClient();
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [formData, setFormData] = useState({});

    const { data: tasks = [], isLoading } = useQuery({
        queryKey: ['tasks'],
        queryFn: async () => {
            const { data } = await axios.get('/maintenance');
            return data;
        },
    });

    const addMutation = useMutation({
        mutationFn: (newTask) => axios.post('/maintenance', {
            ...newTask,
            lastPerformed: newTask?.lastPerformed.toISOString()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
    });

    const updateMutation = useMutation({
        mutationFn: (updatedTask) =>
            axios.patch(`/maintenance/${updatedTask._id}`, {
                task: updatedTask?.task,
                description: updatedTask?.description,
                status: updatedTask?.status,
                intervalValue: updatedTask?.intervalValue,
                lastPerformed: updatedTask?.lastPerformed,
                currentCount: updatedTask?.currentCount,
                currentHours: updatedTask?.currentHours
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => axios.delete(`/maintenance/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
    });

    const handleOpen = (data = null) => {
        setEditData(data);
        setFormData(data || {});
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setFormData({});
    };

    const handleSubmit = () => {
        if (editData) {
            updateMutation.mutate({ ...formData, _id: editData._id });
        } else {
            addMutation.mutate(formData);
        }
        handleClose();
    };

    const handleDelete = (id) => {
        deleteMutation.mutate(id);
    };

    if (isLoading) return <LoadingPage/>;

    return (
        <>
            <Container
                maxWidth={false}
                sx={{
                    width: '100%',
                    padding: { xs: 1, sm: 2, md: 3 },
                    height: '80vh',
                }}
            >
                <Paper
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden', // Ограничивает переполнение
                    }}
                >
                    <Box sx={{
                        padding: { xs: 1, md: 2 },
                        borderBottom: '1px solid #ccc',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <Button
                            startIcon={<Add/>}
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpen()}
                            size={isSmallScreen ? 'small' : 'medium'}
                            sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}
                        >
                            add task
                        </Button>
                    </Box>

                    {/* Нижняя часть с таблицей */}
                    <Box
                        sx={{ flex: 1, ml: 1, overflowX: 'auto', overflowY: 'auto' }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>id</StyledTableCell>
                                    <StyledTableCell>task</StyledTableCell>
                                    <StyledTableCell>interval type</StyledTableCell>
                                    <StyledTableCell>interval value</StyledTableCell>
                                    <StyledTableCell>last performed</StyledTableCell>
                                    <StyledTableCell>due</StyledTableCell>
                                    <StyledTableCell>current value</StyledTableCell>
                                    <StyledTableCell>status</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tasks?.map((task) => (
                                    <TableRow key={task._id}>
                                        <StyledTableCell>{task._id.slice(-5)}</StyledTableCell>
                                        <StyledTableCell>{task.task}</StyledTableCell>
                                        <StyledTableCell>{task?.intervalType}</StyledTableCell>
                                        <StyledTableCell>
                                            {
                                                ( task?.intervalType === MAINTENANCE_TYPE.HOUR && `${task?.intervalValue} h` )
                                                || ( task?.intervalType === MAINTENANCE_TYPE.COUNT && `${task?.intervalValue}` )
                                                || ( task?.intervalType === MAINTENANCE_TYPE.DAY && `${task?.intervalValue} d` )
                                                || ''
                                            }
                                        </StyledTableCell>
                                        <StyledTableCell>{isISO8601(task?.lastPerformed) && formatISOToCustom(task?.lastPerformed)}</StyledTableCell>
                                        <StyledTableCell>{isISO8601(task?.nextDue) && formatISOToCustom(task?.nextDue)}</StyledTableCell>
                                        <StyledTableCell>
                                            {
                                                ( task?.intervalType === MAINTENANCE_TYPE.HOUR && `${task?.currentHours?.toFixed(3)} h` )
                                                || ( task?.intervalType === MAINTENANCE_TYPE.COUNT && `${task?.currentCount}` )
                                                || ''
                                            }
                                        </StyledTableCell>
                                        <StyledTableCell>{task?.status}</StyledTableCell>
                                        <StyledTableCell>
                                            <IconButton size={isSmallScreen ? 'small' : 'medium'} color="primary"
                                                        onClick={() => handleOpen(task)}>
                                                <Edit/>
                                            </IconButton>
                                            <IconButton size={isSmallScreen ? 'small' : 'medium'} color="error"
                                                        onClick={() => handleDelete(task._id)}>
                                                <Delete/>
                                            </IconButton>
                                        </StyledTableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>

                {/* Диалог для добавления/редактирования */}
                <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>{editData ? 'edit task' : 'add task'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="task"
                            value={formData.task || ''}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    task: e.target.value,
                                })
                            }
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="description"
                            value={formData.description || ''}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    description: e.target.value,
                                })
                            }
                        />
                        <FormControl fullWidth>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    views={['year', 'month', 'day', 'hours']}
                                    label="last performed"
                                    ampm={false}
                                    minDateTime={minDate}
                                    maxDateTime={maxDate}
                                    value={formData?.lastPerformed ? dayjs(formData?.lastPerformed) : null}
                                    onChange={(date) =>
                                        setFormData({
                                            ...formData,
                                            lastPerformed: date,
                                        })
                                    }
                                    slotProps={{
                                        textField: {}
                                    }}
                                    format="DD/MM/YYYY HH:mm"
                                    sx={{ width: '100%', mt: 2, mb: 1 }}
                                />
                            </LocalizationProvider>
                        </FormControl>
                        {!editData && ( // add
                            <>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>interval type</InputLabel>
                                    <Select
                                        variant="outlined"
                                        value={formData?.intervalType ?? ''}
                                        onChange={(e) => setFormData({ ...formData, intervalType: e.target.value })}
                                        label="interval type"
                                    >
                                        {MAINTENANCE_TYPE_ARR.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                        <TextField
                            fullWidth
                            margin="normal"
                            type="number"
                            label="interval value"
                            value={formData.intervalValue ?? ''}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    intervalValue: e.target.value,
                                })
                            }
                        />
                        {editData && ( // edit
                            <>
                                {( formData?.intervalType === MAINTENANCE_TYPE.COUNT && (
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="current count"
                                        value={formData?.currentCount ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                setFormData({
                                                    ...formData,
                                                    currentCount: value,
                                                });
                                            }
                                        }}
                                    />
                                ) ) || ( formData?.intervalType === MAINTENANCE_TYPE.HOUR && (
                                    <TextField
                                        fullWidth
                                        label="current hours"
                                        margin="normal"
                                        value={formData?.currentHours ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*\.?\d*$/.test(value)) {
                                                setFormData({
                                                    ...formData,
                                                    currentHours: value,
                                                });
                                            }
                                        }}
                                    />
                                ) )}
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>status</InputLabel>
                                    <Select
                                        variant="outlined"
                                        value={formData?.status || ''}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        label="status"
                                    >
                                        {MAINTENANCE_STATUSES_ARR.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {status}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color="secondary">
                            cancel
                        </Button>
                        <Button onClick={handleSubmit} color="primary" variant="contained">
                            {editData ? 'update' : 'add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </>
    )
        ;
};